/**
 * Tracker Screen Component
 * Historiography progress tracker by era
 */

import React from 'react';
import styles from './TrackerScreen.module.css';

interface EraProgress {
  id: string;
  name: string;
  dates: string;
  icon: string;
  progress: number;
  locked: boolean;
}

interface TrackerScreenProps {
  eras: EraProgress[];
  onBackToGame: () => void;
  onBackToReview: () => void;
  labels: {
    title: string;
    subtitle: string;
    backToGame: string;
    backToReview: string;
    locked: string;
    aria: {
      screen: string;
      eraItem: (name: string, percent: number, locked: boolean) => string;
      eraProgress: (percent: number) => string;
      backToGame: string;
      backToReview: string;
    };
  };
}

export const TrackerScreen: React.FC<TrackerScreenProps> = ({
  eras,
  onBackToGame,
  onBackToReview,
  labels,
}) => {
  return (
    <section
      id="screen-tracker"
      className={`screen ${styles.screen} active`}
      role="main"
      aria-label={labels.aria.screen}
    >
      <div className={styles.trackerHeader}>
        <h2>📜 {labels.title}</h2>
        <p>{labels.subtitle}</p>
      </div>

      <div className={styles.periodList} role="list">
        {eras.map((era) => (
          <div
            key={era.id}
            className={styles.periodItem}
            role="listitem"
            aria-label={labels.aria.eraItem(era.name, era.progress, era.locked)}
          >
            <div className={styles.periodIcon} aria-hidden="true">
              {era.icon}
            </div>
            <div className={styles.periodInfo}>
              <div className={styles.periodName}>{era.name}</div>
              <div className={styles.periodDates}>{era.dates}</div>
              <div
                className={styles.periodProgress}
                role="progressbar"
                aria-valuenow={era.progress}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={labels.aria.eraProgress(era.progress)}
              >
                <div
                  className={styles.periodProgressFill}
                  style={{ width: `${era.progress}%` }}
                />
              </div>
            </div>
            {era.locked ? (
              <span className={styles.periodLock} aria-label={labels.locked}>
                🔒
              </span>
            ) : (
              <span className={styles.periodUnlock}>{era.progress}% ✓</span>
            )}
          </div>
        ))}
      </div>

      <div className={styles.footer}>
        <button
          className={styles.backBtn}
          onClick={onBackToGame}
          aria-label={labels.aria.backToGame}
        >
          {labels.backToGame}
        </button>
        <button
          className={styles.backBtn}
          onClick={onBackToReview}
          aria-label={labels.aria.backToReview}
        >
          {labels.backToReview}
        </button>
      </div>
    </section>
  );
};

// Default eras data
export const DEFAULT_ERAS: EraProgress[] = [
  {
    id: 'kievan-rus',
    name: "Kievan Rus' & Early Principalities",
    dates: '862 – 1240',
    icon: '🏰',
    progress: 80,
    locked: false,
  },
  {
    id: 'gdl',
    name: 'Grand Duchy of Lithuania',
    dates: '1236 – 1569',
    icon: '⚔️',
    progress: 65,
    locked: false,
  },
  {
    id: 'commonwealth',
    name: 'Polish–Lithuanian Commonwealth',
    dates: '1569 – 1795',
    icon: '👑',
    progress: 40,
    locked: false,
  },
  {
    id: 'imperial',
    name: 'Russian Imperial Period',
    dates: '1795 – 1918',
    icon: '🦅',
    progress: 20,
    locked: false,
  },
  {
    id: 'interwar',
    name: 'Interwar Independence',
    dates: '1918 – 1940',
    icon: '🕊️',
    progress: 0,
    locked: true,
  },
  {
    id: 'soviet',
    name: 'Soviet Period',
    dates: '1940 – 1991',
    icon: '⭐',
    progress: 0,
    locked: true,
  },
  {
    id: 'modern',
    name: 'Independence & Modern Scholarship',
    dates: '1991 – Present',
    icon: '🌟',
    progress: 0,
    locked: true,
  },
];

export default TrackerScreen;
